import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View, Pressable, Platform, PanResponder } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useUIStore } from '@/store';
import { clampContextSidebarWidth, getEffectiveContextSidebarWidth, getNavigationSidebarWidth, isArtifactSidebarType, resolveArtifactSidebarWidth } from '@/constants/config';
import WorkArtifactRevisionNotice from '@/components/Files/WorkArtifactRevisionNotice';
import useMacModalFocus from '@/hooks/useMacModalFocus';
import { MAC_HEADER_HEIGHT } from '@/lib/macTitlebar';

const DEFAULT_WIDTH = 320;

const createStyles = (colors, pwaSafeAreaTop) => StyleSheet.create({
  container: {
    width: DEFAULT_WIDTH,
    flexShrink: 0,
    minWidth: 0,
    height: '100%',
    backgroundColor: colors.solid_color_block,
    borderLeftWidth: 1,
    borderLeftColor: colors.solid_color_block,
    overflow: 'hidden',
    // Pin to the right of the chat row so a flex child that will not
    // shrink cannot push this panel past the viewport (HTML Work previews).
    ...(Platform.OS === 'web' ? {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: 20,
    } : { position: 'relative' }),
    paddingTop: Platform.OS === 'web' ? pwaSafeAreaTop : 0,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.solid_color_block,
    zIndex: 99,
  },
  animated: {
    transition: 'all 0.24s ease-in-out',
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: -5,
    width: 10,
    height: '100%',
    cursor: 'ew-resize',
    zIndex: 2,
  },
  resizeHandleActive: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
});

function ContextualSidebar({ 
  isVisible, 
  onClose, 
  width = DEFAULT_WIDTH,
  children,
  pwaSafeAreaTop = 0,
}) {
  const { colors } = useTheme();
  const { isXS, windowWidth } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, pwaSafeAreaTop), [colors, pwaSafeAreaTop]);

  const storeWidth = useUIStore(state => getEffectiveContextSidebarWidth(state, windowWidth));
  const navWidth = useUIStore(state => getNavigationSidebarWidth(state, windowWidth));
  const setStoreWidth = useUIStore(state => state.setContextSidebarWidth);
  const sidebarType = useUIStore(state => state.contextSidebarType);
  const effectiveWidth = typeof storeWidth === 'number' ? storeWidth : width;
  const macOverlay = Platform.OS === 'macos' && isVisible && effectiveWidth === 0;
  const savedWidth = useUIStore(state => state.contextSidebarWidth);
  const displayedWidth = macOverlay ? Math.min(savedWidth || width, windowWidth - 24) : effectiveWidth;
  const panelVisible = isVisible && displayedWidth > 0;
  const panelRef = useRef(null);
  const syncPanelFocus = useMacModalFocus(panelRef, macOverlay, onClose);
  const Wrapper = Platform.OS === 'macos' ? View : React.Fragment;
  const wrapperProps = Platform.OS === 'macos' ? {
    style: macOverlay ? [StyleSheet.absoluteFillObject, { zIndex: 90 }]
      : { width: panelVisible ? displayedWidth : 0, height: '100%', minWidth: 0 },
    onAccessibilityEscape: macOverlay ? onClose : undefined,
  } : {};

  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef(0);
  const resizeState = useRef(null);
  resizeState.current = { displayedWidth, setStoreWidth, sidebarType, windowWidth, navWidth };
  const nativeResize = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: () => { resizeStart.current = resizeState.current.displayedWidth; setIsResizing(true); },
    onPanResponderMove: (_, gesture) => {
      const current = resizeState.current;
      current.setStoreWidth(resolveArtifactSidebarWidth(current.sidebarType, current.windowWidth, current.navWidth, resizeStart.current - gesture.dx), current.sidebarType);
    },
    onPanResponderRelease: () => setIsResizing(false),
    onPanResponderTerminate: () => setIsResizing(false),
  }), []);

  const handleMouseDown = useCallback((e) => {
    if (Platform.OS !== 'web') return;
    e.preventDefault();
    setIsResizing(true);

    // Drag limits follow the panel that is open, so Details cannot be dragged
    // out to an editor's width (and vice versa).
    const widthFromEvent = (event) => isArtifactSidebarType(sidebarType)
      ? resolveArtifactSidebarWidth(sidebarType, window.innerWidth, navWidth, window.innerWidth - event.clientX)
      : clampContextSidebarWidth(sidebarType, window.innerWidth - event.clientX);

    let rafId;
    const handleMouseMove = (moveEvent) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setStoreWidth(widthFromEvent(moveEvent), sidebarType);
      });
    };

    const handleMouseUp = (upEvent) => {
      setIsResizing(false);
      if (rafId) cancelAnimationFrame(rafId);
      setStoreWidth(widthFromEvent(upEvent), sidebarType);
      if (Platform.OS === 'web') {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
    };

    if (Platform.OS === 'web') {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  }, [setStoreWidth, sidebarType, navWidth]);

  useEffect(() => {
    if (Platform.OS !== 'macos' && isXS && isVisible) onClose?.();
  }, [isXS, isVisible, onClose]);

  if (isXS && Platform.OS !== 'macos') return null;

  return (
    <Wrapper {...wrapperProps}>
      {macOverlay && <Pressable accessibilityRole="button" accessibilityLabel="Close panel" onPress={onClose}
        style={[StyleSheet.absoluteFillObject, { backgroundColor: 'rgba(0,0,0,0.28)' }]} />}
      {/* Sidebar */}
      <View key="panel" ref={panelRef} {...(Platform.OS === 'web' ? {} : { collapsable: false })} onLayout={syncPanelFocus}
        accessibilityViewIsModal={macOverlay} style={[
        styles.container,
        !isResizing && styles.animated,
        {
          width: panelVisible ? displayedWidth : 0,
          borderLeftWidth: panelVisible ? 1 : 0,
          transition: isResizing ? 'none' : undefined,
          // Hidden or sliding-off panels must not steal Send clicks on /app.
          pointerEvents: panelVisible ? 'auto' : 'none',
          transform: Platform.OS === 'web' 
            ? [{ translateX: isVisible ? '0px' : `${effectiveWidth}px` }]
            : undefined,
        },
        macOverlay && { position: 'absolute', top: MAC_HEADER_HEIGHT, bottom: 0, right: 0, height: 'auto' },
      ]}>
        {/* Resize handle appears on the left edge */}
        {Platform.OS === 'web' && isVisible && (
          <View 
            style={[styles.resizeHandle, isResizing && styles.resizeHandleActive]}
            onMouseDown={handleMouseDown}
          />
        )}
        {Platform.OS === 'macos' && sidebarType === 'project-editor' && panelVisible && !macOverlay && (
          <View accessibilityLabel="Resize project editor" {...nativeResize.panHandlers}
            style={[styles.resizeHandle, { left: 0, width: 5 }, isResizing && styles.resizeHandleActive]} />
        )}
        <WorkArtifactRevisionNotice />
        {children}
      </View>
    </Wrapper>
  );
}

export default ContextualSidebar;
